<script setup>
/**
 * Icon —— 统一图标组件（设计规范 3.6）。
 *
 * - 线性 2px 描边、圆角端点，颜色通过 currentColor 继承。
 * - 尺寸变体 16/20/24/32/48px，由 size prop 控制。
 * - 图标定义集中在 PATHS，新增图标只需在此追加。
 */
import { computed } from 'vue'

const props = defineProps({
  name: { type: String, required: true },
  size: { type: [Number, String], default: 24 },
})

/** 每个图标为一组 <path>/<circle> 的原始 d 定义。 */
const PATHS = {
  play: ['M8 5v14l11-7z'],
  pause: ['M9 4h3v16H9z', 'M15 4h3v16h-3z'],
  'volume-high': ['M4 9v6h4l5 4V5L8 9H4z', 'M16 8a5 5 0 0 1 0 8', 'M19 5a9 9 0 0 1 0 14'],
  'volume-mute': ['M4 9v6h4l5 4V5L8 9H4z', 'M17 9l6 6', 'M23 9l-6 6'],
  fullscreen: ['M4 9V4h5', 'M20 9V4h-5', 'M4 15v5h5', 'M20 15v5h-5'],
  'fullscreen-exit': ['M9 4v5H4', 'M15 4v5h5', 'M9 20v-5H4', 'M15 20v-5h5'],
  back: ['M15 5l-7 7 7 7'],
  'chevron-right': ['M9 5l7 7-7 7'],
  close: ['M6 6l12 12', 'M18 6L6 18'],
  trash: ['M4 7h16', 'M9 7V4h6v3', 'M7 7l1 14h8l1-14', 'M10 11v6', 'M14 11v6'],
  menu: ['M4 6h16', 'M4 12h16', 'M4 18h16'],
  'layout-list': ['M5 5h14v14H5z'],
  'layout-grid': ['M4 4h6v6H4z', 'M14 4h6v6h-6z', 'M4 14h6v6H4z', 'M14 14h6v6h-6z'],
  search: ['M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14z', 'M16 16l4 4'],
  edit: ['M4 20h4l11-11-4-4L4 16v4z', 'M13.5 6.5l4 4'],
  mail: ['M4 5h16v14H4z', 'M4 7l8 6 8-6'],
  video: ['M3 6h13v12H3z', 'M16 10l5-3v10l-5-3z'],
  'credit-card': ['M3 6h18v12H3z', 'M3 10h18', 'M7 15h3'],
  'file-text': ['M6 3h8l4 4v14H6z', 'M14 3v5h5', 'M9 13h6', 'M9 17h6'],
  'shield-check': ['M12 3l8 3v5c0 5-3.4 8.6-8 10-4.6-1.4-8-5-8-10V6z', 'M8.5 12l2.2 2.2 4.8-5'],
  languages: ['M4 5h9', 'M8.5 3v2', 'M6 8c1.2 2.5 3.3 4.4 6 5.5', 'M11 8c-1 3.2-3.4 5.8-6.5 7', 'M14 21l4-10 4 10', 'M15.5 17h5'],
  'log-out': ['M10 4H5v16h5', 'M14 8l4 4-4 4', 'M8 12h10'],
  'message-circle': ['M21 11.5a8.5 8.5 0 0 1-9 8.5 9.5 9.5 0 0 1-4-.9L3 21l1.7-4.5A8.5 8.5 0 1 1 21 11.5z'],
  'external-link': ['M14 4h6v6', 'M20 4l-9 9', 'M18 13v7H4V6h7'],
  discord: ['M8.2 7.2A12 12 0 0 1 12 6.6a12 12 0 0 1 3.8.6', 'M7.1 5.2c-2.3.7-3.5 2-3.5 2S2 10.5 2 15c1.5 2 3.4 3 3.4 3l1.4-1.8', 'M16.9 5.2c2.3.7 3.5 2 3.5 2S22 10.5 22 15c-1.5 2-3.4 3-3.4 3l-1.4-1.8', 'M7.5 16c1.4 1 3 1.4 4.5 1.4s3.1-.4 4.5-1.4', 'M8.5 12h.01', 'M15.5 12h.01'],
  x: ['M5 4l14 16', 'M19 4l-5.7 6.5', 'M10.7 13.5 5 20'],
  facebook: ['M14 22v-9h3l1-4h-4V7c0-1.2.8-2 2.2-2H19V1.5h-3.5C11.5 1.5 9 3.8 9 7v2H5v4h4v9'],
  youtube: ['M21 8.2a2.8 2.8 0 0 0-2-2C17.2 5.7 12 5.7 12 5.7s-5.2 0-7 .5a2.8 2.8 0 0 0-2 2A29 29 0 0 0 2.5 12a29 29 0 0 0 .5 3.8 2.8 2.8 0 0 0 2 2c1.8.5 7 .5 7 .5s5.2 0 7-.5a2.8 2.8 0 0 0 2-2 29 29 0 0 0 .5-3.8 29 29 0 0 0-.5-3.8z', 'M10 9l5 3-5 3z'],
  instagram: ['M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5z', 'M12 8a4 4 0 1 1 0 8 4 4 0 0 1 0-8z', 'M17.5 6.5h.01'],
  tiktok: ['M14 3v11.5a4.5 4.5 0 1 1-4.5-4.5', 'M14 3c.8 3.2 2.8 5 6 5'],
  microphone: ['M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3z', 'M5 11a7 7 0 0 0 14 0', 'M12 18v3', 'M8 21h8'],
  keyboard: ['M4 6h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z', 'M6 10h.01', 'M10 10h.01', 'M14 10h.01', 'M18 10h.01', 'M6 14h.01', 'M10 14h8'],
  restart: ['M4 12a8 8 0 1 1 2.3 5.6', 'M4 20v-5h5'],
  home: ['M4 11l8-7 8 7', 'M6 10v10h12V10'],
  'plus-circle': ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z', 'M12 8v8', 'M8 12h8'],
  user: ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M4 21a8 8 0 0 1 16 0'],
  save: ['M6 3h12a2 2 0 0 1 2 2v16l-8-4-8 4V5a2 2 0 0 1 2-2z', 'M8 7h8'],
  download: ['M12 3v12', 'M7 10l5 5 5-5', 'M5 21h14'],
  bookmark: ['M6 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v17l-6-4-6 4V4z'],
  star: ['M12 2.8l2.8 5.7 6.3.9-4.6 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2-4.6-4.4 6.3-.9L12 2.8z'],
  review: ['M4 5.5A2.5 2.5 0 0 1 6.5 3H20v17H6.5A2.5 2.5 0 0 1 4 17.5z', 'M8 7h8', 'M8 11h6', 'M8 15h5'],
  history: ['M3 12a9 9 0 1 0 3-6.7', 'M3 4v5h5', 'M12 7v5l3 2'],
  settings: [
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z',
  ],
  clue: ['M11 4a7 7 0 0 1 4 12.7V19H9v-2.3A7 7 0 0 1 11 4z', 'M9 22h6'],
  check: ['M5 12l5 5L20 7'],
  alert: ['M12 3l9 16H3z', 'M12 10v4', 'M12 17h.01'],
  loading: ['M12 4a8 8 0 1 0 8 8'],
  share: ['M15 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'M6 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'M15 22a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'M8.6 13.5l6.8 4', 'M15.4 6.5l-6.8 4'],
  'share-forward': ['M14 5l6 6-6 6v-4C8 13 5 16 4 20c0-7 3-12 10-13V5z'],
  gamepad: ['M8 8h8a5 5 0 0 1 4.7 6.7l-1 3A2 2 0 0 1 16.5 19L14 17h-4l-2.5 2a2 2 0 0 1-3.2-1.3l-1-3A5 5 0 0 1 8 8z', 'M8 11v4', 'M6 13h4', 'M15.5 12h.01', 'M18 14h.01'],
  map: ['M9 18 3 21V6l6-3 6 3 6-3v15l-6 3-6-3z', 'M9 3v15', 'M15 6v15'],
  objectives: ['M8 6h12', 'M8 12h12', 'M8 18h12', 'M3.5 6l1.2 1.2L6.5 5', 'M3.5 12l1.2 1.2L6.5 11', 'M3.5 18l1.2 1.2L6.5 17'],
  eye: ['M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z', 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z'],
  'eye-off': ['M9.88 9.88a3 3 0 1 0 4.24 4.24', 'M10.73 5.08A10.43 10.43 0 0 1 12 5c6 0 9.5 7 9.5 7a16.3 16.3 0 0 1-3.36 4.24', 'M6.61 6.61A17.1 17.1 0 0 0 2.5 12s3.5 7 9.5 7a9.7 9.7 0 0 0 3.39-.61', 'M3 3l18 18'],
  heart: ['M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.7-7.5 1.1-1.1a5.5 5.5 0 0 0 0-7.8z'],
  fire: ['M12 22c4.4 0 8-3.6 8-8 0-3.1-1.7-6-5-8.5.2 2-1 3.5-2.2 4.2.2-3.5-1.6-6.6-4.8-8.7.2 2.7-1.3 4.7-2.6 6.4C4.8 11 4 12.5 4 14c0 4.4 3.6 8 8 8z', 'M9.5 17.5c0 1.4 1.1 2.5 2.5 2.5s2.5-1.1 2.5-2.5c0-1.2-.8-2.3-2.5-3.5-1.7 1.2-2.5 2.3-2.5 3.5z'],
  bell: ['M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9z', 'M10 21h4'],
  coin: ['M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M12 7v10', 'M9 10.5h4.5a2 2 0 0 1 0 4H10', 'M10 7h5'],
}

const paths = computed(() => PATHS[props.name] || [])
const px = computed(() => `${props.size}px`)
</script>

<template>
  <svg
    class="vg-icon"
    :style="{ width: px, height: px }"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path v-for="(d, i) in paths" :key="i" :d="d" />
  </svg>
</template>

<style scoped>
.vg-icon {
  display: inline-block;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  flex-shrink: 0;
}
</style>
